import React, { Component } from 'react';
import EmployeeAccountModal from './EmployeeAccountModal';

var config = process.env;

/**
 * Cuenta del equipo — el panel del turno (backend/docs/spec-cuenta-empleados.md §7.1, FR-030).
 *
 * Vive en la pantalla de Caja y no en el POS, y eso es una decisión, no una casualidad (D-06):
 * cobrar no es servir. En el POS hay alguien esperando su pedido; registrar un pago es un acto de
 * caja, y esta pantalla es donde ya se abre, se cuenta y se cargan los movimientos del turno.
 *
 * Es además la red de contención de toda la feature: si nadie registra los pagos, el saldo deja de
 * reflejar la realidad. Por eso el panel se ve todos los días —está en la pantalla que se abre
 * todos los días— y muestra hace cuántos días que cada uno no paga.
 *
 * Con la caja CERRADA el panel se sigue viendo: lo que no se puede es registrar (FR-036). Ver el
 * saldo no mueve plata.
 */
class EmployeeAccountPanel extends Component {

    state = { accounts: [], totalDebt: 0, cargando: true, error: null, cobrando: null };

    componentDidMount() {
        this.cargar();
    }

    money = (n) => `$${Number(n || 0).toLocaleString('de-DE')}`;

    // Días desde el último pago. Es el dato que convierte "debe $300" en "debe $300 hace tres
    // semanas", que es lo accionable.
    diasDesde = (iso) => {
        if (!iso) return null;
        const dias = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
        return Number.isFinite(dias) && dias >= 0 ? dias : null;
    };

    cargar = () => {
        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}employeeAccount`, {
            headers: { 'Content-Type': 'application/json', 'access-token': localStorage.getItem('token') },
        })
            .then((r) => r.json())
            .then((data) => {
                if (data && data.error === 0 && data.data) {
                    this.setState({
                        accounts: data.data.accounts || [],
                        totalDebt: data.data.totalDebt || 0,
                        cargando: false, error: null,
                    });
                    return;
                }
                this.setState({ cargando: false, error: (data && data.message) || 'No se pudo cargar la cuenta del equipo.' });
            })
            .catch(() => this.setState({ cargando: false, error: 'No se pudo cargar la cuenta del equipo.' }));
    };

    // Tras registrar: se recarga el panel Y se avisa al padre, para que el esperado del cierre y
    // los movimientos del turno queden al día en la misma pantalla.
    onPagoRegistrado = () => {
        this.setState({ cobrando: null }, () => {
            this.cargar();
            if (this.props.onChange) this.props.onChange();
        });
    };

    saldoTexto = (balance) => {
        if (balance > 0) return this.money(balance);
        if (balance < 0) return `${this.money(-balance)} a favor`;
        return 'Al día';
    };

    render() {
        const { cajaAbierta, responsable } = this.props;
        const { accounts, cargando, error, cobrando } = this.state;

        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom d-flex justify-content-between align-items-center">
                    <span>Cuenta del equipo</span>
                    {this.state.totalDebt > 0 && (
                        <small className="text-muted">
                            Deuda total {this.money(this.state.totalDebt)}
                        </small>
                    )}
                </div>

                <div className="card-body">
                    {error && (
                        <div className="alert alert-danger py-2 mb-2" style={{ fontSize: 13 }}>{error}</div>
                    )}

                    {cargando && <small className="text-muted">Cargando…</small>}

                    {!cargando && accounts.length === 0 && (
                        <small className="text-muted">
                            No hay nadie catalogado como empleado. Se marca desde la ficha del cliente.
                        </small>
                    )}

                    {accounts.map((a) => {
                        const dias = this.diasDesde(a.lastPaymentAt);
                        return (
                            <div
                                key={a.cedula}
                                className="d-flex align-items-center justify-content-between py-2"
                                style={{ borderBottom: '1px solid #f0f2f5', gap: 12 }}
                            >
                                <div className="d-flex flex-column" style={{ minWidth: 0 }}>
                                    <span style={{ fontSize: 14 }}>
                                        {a.name || a.cedula}
                                        {!a.isEmployee && (
                                            <span className="text-muted" style={{ fontSize: 12 }}> · ya no es empleado</span>
                                        )}
                                    </span>
                                    <small className="text-muted">
                                        {dias === null
                                            ? 'sin pagos registrados'
                                            : (dias === 0 ? 'pagó hoy' : `último pago hace ${dias} día${dias === 1 ? '' : 's'}`)}
                                    </small>
                                </div>

                                <span
                                    className={a.balance > 0 ? (a.warn ? 'text-danger font-weight-bold' : '') : 'text-success'}
                                    style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                                >
                                    {this.saldoTexto(a.balance)}
                                </span>

                                <button
                                    className="btn btn-sm btn-white"
                                    style={{ whiteSpace: 'nowrap' }}
                                    disabled={!cajaAbierta}
                                    title={cajaAbierta ? '' : 'Abrí la caja para registrar un pago'}
                                    onClick={() => this.setState({ cobrando: a })}
                                >
                                    Saldar deuda
                                </button>
                            </div>
                        );
                    })}

                    {!cargando && !cajaAbierta && accounts.length > 0 && (
                        <small className="text-muted d-block mt-2">
                            Con la caja cerrada los saldos se ven pero no se registran pagos: el asiento
                            se sella contra la caja abierta.
                        </small>
                    )}
                </div>

                {cobrando && (
                    <EmployeeAccountModal
                        account={cobrando}
                        responsable={responsable}
                        cajaAbierta={cajaAbierta}
                        onClose={() => this.setState({ cobrando: null })}
                        onDone={this.onPagoRegistrado}
                    />
                )}
            </div>
        );
    }
}

export default EmployeeAccountPanel;
